import { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import { Analytics } from '@trello/atlassian-analytics';
import {
  BoardIdProvider,
  CardIdProvider,
  WorkspaceIdProvider,
} from '@trello/id-context';
import { Button } from '@trello/nachos/button';
import { RouterLink } from '@trello/router/router-link';
import type { NotificationTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { ContentRenderer } from 'app/src/components/ContentRenderer';
import type { CommentEntity } from './types';

import styles from './Comment.less';

interface CommentProps
  extends Pick<
    CommentEntity,
    'actionUrl' | 'canTruncate' | 'defaultIsTruncated' | 'text' | 'textData'
  > {
  isFullWidth?: boolean;
  reactionAdded?: boolean;
  naturalLinkStyling?: boolean;
  cardId?: string;
  boardId?: string;
  workspaceId?: string;
  onClick?: () => void;
  commentId?: string;
}

interface CommentState {
  isTruncated?: boolean;
}
// TODO: EDIT comment

export class Comment extends Component<CommentProps, CommentState> {
  constructor(props: CommentProps) {
    super(props);

    this.state = {
      isTruncated: !!this.props.canTruncate && !!this.props.defaultIsTruncated,
    };
  }

  collapseComment = () => {
    this.setState({ isTruncated: true });
    Analytics.sendClickedButtonEvent({
      buttonName: 'hideFullCommentButton',
      source: 'notificationsInlineDialog',
    });
  };

  expandComment = () => {
    this.setState({ isTruncated: false });
    Analytics.sendClickedButtonEvent({
      buttonName: 'showFullCommentButton',
      source: 'notificationsInlineDialog',
    });
  };

  render() {
    const {
      actionUrl,
      canTruncate,
      commentId,
      isFullWidth = false,
      reactionAdded = false,
      naturalLinkStyling = false,
      cardId = '',
      boardId = '',
      workspaceId = '',
      onClick,
      text,
      textData,
    } = this.props;

    const { isTruncated } = this.state;

    const options = {
      textData: JSON.parse(textData ?? '{}'),
    };

    const commentContent = (
      <div
        className={classNames({
          [styles.currentComment]: true,
          [styles.collapsedComment]: canTruncate && isTruncated,
          [styles.naturalLinkStyling]: naturalLinkStyling,
          [styles.reducedRendererPadding]: true,
        })}
      >
        <WorkspaceIdProvider value={workspaceId}>
          <BoardIdProvider value={boardId}>
            <CardIdProvider value={cardId}>
              <ContentRenderer
                contentType="comment"
                text={text}
                emoji={options.textData.emoji}
                contentId={commentId}
              />
            </CardIdProvider>
          </BoardIdProvider>
        </WorkspaceIdProvider>
      </div>
    );

    return (
      <div
        className={styles.commentContainer}
        data-testid={getTestId<NotificationTestIds>('comment-container')}
      >
        <div
          className={classNames({
            [styles.comment]: true,
            [styles.isTruncated]: isTruncated,
            [styles.isFullWidth]: isFullWidth,
            [styles.reactionAdded]: reactionAdded,
          })}
        >
          {canTruncate && isTruncated ? (
            <>
              <div className={styles.truncationOverlay} />
              <Button
                appearance="subtle-link"
                className={styles.showMoreButton}
                onClick={this.expandComment}
                size="fullwidth"
              >
                <FormattedMessage
                  id="templates.notification.show-more"
                  defaultMessage="Show more"
                  description="The title of the button used to expand a truncated comment"
                />
              </Button>
            </>
          ) : null}
          {actionUrl ? (
            <RouterLink href={actionUrl} onClick={onClick}>
              {commentContent}
            </RouterLink>
          ) : (
            commentContent
          )}
          {canTruncate && !isTruncated ? (
            <Button
              appearance="subtle-link"
              className={styles.showLessButton}
              onClick={this.collapseComment}
              size="fullwidth"
            >
              <FormattedMessage
                id="templates.notification.show-less"
                defaultMessage="Show less"
                description="The title of the button used to collapse an expanded comment"
              />
            </Button>
          ) : null}
        </div>
      </div>
    );
  }
}
